import React from 'react';
import { useNavigate } from 'react-router-dom';

import { AsideHeader, FooterItem, type MenuItem } from '@gravity-ui/navigation';
import { Select, type SelectOption, Switch } from '@gravity-ui/uikit';
import { House, ArrowRightToSquare, CirclePlus, Person, GearDot, Bell } from '@gravity-ui/icons';

import { type Lang } from '@lib/i18n';
import { useClientContext } from '@client/lib/ClientContext';
import { Const } from '@const/public';
import { CommonConst } from '@const/index';

import { updateLang } from './Header.utils/updateLang';
import { toggleTheme } from './Header.utils/toggleTheme';
import type { IHeaderProps } from './Header.typings';
import { headerCn, headerLanguageCn, headerSwitchCn } from './Header.const';
import { Logo } from './Logo';

import { i18n } from './Header.i18n';

import './Header.scss';
import './Header@desktop.scss';

const selectOptions: SelectOption<Lang>[] = [
    { content: '🇷🇺', value: 'ru' },
    { content: '🇺🇸', value: 'en' },
    { content: '🇬🇪', value: 'ka' },
];

export const Header: React.FC<IHeaderProps> = props => {
    const navigate = useNavigate();
    const [{ userInfo, lang, theme, authKey }, dispatchContext] = useClientContext();

    const handleItemClick = (item: MenuItem) => {
        navigate(item.id);
    };

    const notAuthMenu: MenuItem[] = [
        { id: '/', title: i18n('index'), icon: House, onItemClick: handleItemClick, tooltipText: i18n('index') },
        { id: 'auth', title: i18n('auth'), icon: ArrowRightToSquare, link: `https://t.me/${Const.TELEGRAM_BOT_USERNAME}?start=${authKey}`, type: 'action', tooltipText: i18n('auth') },
    ];

    const authMenu: MenuItem[] = [
        { id: '/', title: i18n('index'), icon: House, onItemClick: handleItemClick, tooltipText: i18n('index') },
        { id: '/publication/new', title: i18n('publicationNew'), icon: CirclePlus, onItemClick: handleItemClick, tooltipText: i18n('publicationNew') },
        { id: '/account', title: i18n('account'), icon: Person, onItemClick: handleItemClick, tooltipText: i18n('account') },
        { id: '/notify', title: i18n('notify'), icon: Bell, onItemClick: handleItemClick, tooltipText: i18n('notify') },
    ];

    const [menuItems, setMenuItems] = React.useState<MenuItem[]>(notAuthMenu);
    const [compact, setCompact] = React.useState<boolean>(true);

    React.useEffect(() => {
        setMenuItems(userInfo ? authMenu : notAuthMenu);
    }, [userInfo, lang]);

    const onUpdateLang = React.useCallback((value: string[]) => {
        updateLang(value[0], dispatchContext);
    }, []);

    const onThemeToggle = React.useCallback((isDark: boolean) => {
        toggleTheme(isDark, dispatchContext);
    }, []);

    const renderFooter = (data: {
        size: number;
        compact: boolean;
        asideRef: React.RefObject<HTMLDivElement>;
    }) => {
        if (data.compact) {
            return (
                <FooterItem compact={compact} item={{
                    id: 'settings',
                    title: i18n('settings'),
                    tooltipText: i18n('settings'),
                    hidden: compact,
                    icon: GearDot,
                    onItemClick: () => {
                        setCompact(!compact);
                    },
                }}
                />
            );
        }

        return (
            <FooterItem
                compact={compact}
                item={{
                    id: 'settings',
                    title: i18n('settings'),
                    tooltipText: i18n('settings'),
                    hidden: compact,
                    itemWrapper: () => (
                        <>
                            <Select
                                className={headerLanguageCn}
                                value={[lang]}
                                onUpdate={onUpdateLang}
                                options={selectOptions}
                                size="xl"
                            />
                            <Switch
                                className={headerSwitchCn}
                                content={i18n('theme')}
                                checked={theme === 'dark'}
                                onUpdate={onThemeToggle}
                                size="l"
                            />
                        </>
                    ),
                }}
            />
        );
    };

    const handleClickLogo = () => {
        navigate('/');
    };

    return (
        <AsideHeader
            className={headerCn}
            logo={{
                text: CommonConst.projectName,
                onClick: handleClickLogo,
                iconSrc: Logo,
                textSize: 18,
            }}
            menuItems={menuItems}
            compact={compact}
            onChangeCompact={setCompact}
            renderFooter={renderFooter}
            renderContent={() => props.children}
            multipleTooltip
            headerDecoration
        />
    );
};

